import axios from 'axios';

import { URL_CONSTANTS } from '../../../constants/urlConstants';
import { formatPhoneNumber } from '../../../utils/Helpers';
import {
  CallLogDbPayload,
  RingCentralCallLogRecord,
  buildCallLogPostBody,
  cacheTokenizedRecording,
  fetchCallRecordingBlob,
  mapRcCallLogToDbPayload,
  isCallLogRecordReady,
  collectCallDedupeKeys,
} from '../../../utils/ringCentralCallHistory';
import {
  clearInboundCallContext,
  clearOutboundCallContext,
  getCallLogCustomerId,
  getInboundCallLogProjectId,
  getCallLogProjectId,
  hasInboundCallContext,
  hasOutboundCallContext,
  isInboundCallDirection,
  triggerCallLoggerMatch,
} from '../../../utils/ringCentralDialer';
import {
  CALL_HISTORY_EMPTY_MESSAGES,
  CALL_HISTORY_EMPTY_STATS,
  CALL_HISTORY_EMPTY_DIRECTION_COUNTS,
  CALL_HISTORY_PAGE_SIZE,
} from '../callHistory.constants';
import {
  ApiCallLog,
  CallHistoryDirectionCounts,
  CallHistoryResult,
  CallHistoryStats,
} from '../callHistory.types';

const CRM = URL_CONSTANTS.CRM;

export type CallLogListOptions = {
  customerId?: string | number;
  projectId?: string | number;
  clientIds?: string[];
  search?: string;
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: string;
};

export const CALL_HISTORY_REFRESH_EVENT = 'pf:call-history-refresh';

const REFRESH_DEBOUNCE_MS = 800;
let refreshDebounceTimer: number | null = null;

export const dispatchCallHistoryRefresh = () => {
  if (refreshDebounceTimer) {
    window.clearTimeout(refreshDebounceTimer);
  }
  refreshDebounceTimer = window.setTimeout(() => {
    refreshDebounceTimer = null;
    window.dispatchEvent(new CustomEvent(CALL_HISTORY_REFRESH_EVENT));
  }, REFRESH_DEBOUNCE_MS);
};

const isConfigured = () => Boolean(CRM.baseUrl);

const errorResult = (message: string): CallHistoryResult => ({
  error: true,
  message,
  data: [],
  count: 0,
  directionCounts: CALL_HISTORY_EMPTY_DIRECTION_COUNTS,
});

const buildPagination = (options: CallLogListOptions = {}) => ({
  offset: options.offset ?? 0,
  limit: options.limit ?? CALL_HISTORY_PAGE_SIZE,
  sortField: options.sortField ?? 'startTime',
  sortOrder: options.sortOrder ?? 'DESC',
});

const buildSearchParam = (search?: string) => {
  const trimmed = search?.trim();
  return trimmed ? { search: trimmed } : {};
};

const parseListResponse = (payload: unknown) => {
  if (Array.isArray(payload)) {
    const rows = payload as ApiCallLog[];
    return {
      rows,
      count: rows.length,
      directionCounts: CALL_HISTORY_EMPTY_DIRECTION_COUNTS,
    };
  }

  const body = payload as {
    data?: ApiCallLog[];
    result?: ApiCallLog[];
    records?: ApiCallLog[];
    count?: number;
    total?: number;
    totalCount?: number;
    inboundCount?: number;
    outboundCount?: number;
    missedCount?: number;
  };
  const rows = body?.data || body?.result || body?.records || [];
  const safeRows = Array.isArray(rows) ? rows : [];
  const totalCount =
    body?.totalCount ?? body?.count ?? body?.total ?? safeRows.length;

  const directionCounts: CallHistoryDirectionCounts = {
    totalCount,
    inboundCount: body?.inboundCount ?? 0,
    outboundCount: body?.outboundCount ?? 0,
    missedCount: body?.missedCount ?? 0,
  };

  return {
    rows: safeRows,
    count: totalCount,
    directionCounts,
  };
};

const fetchCallHistoryFromCrm = async (
  options: CallLogListOptions = {}
): Promise<CallHistoryResult> => {
  if (!isConfigured()) {
    return errorResult(CALL_HISTORY_EMPTY_MESSAGES.unavailable);
  }

  try {
    const pagination = buildPagination(options);
    const clientIds = (options.clientIds || []).filter(Boolean);
    const searchParam = buildSearchParam(options.search);
    const response =
      clientIds.length > 0
        ? await axios.post(
            `${CRM.baseUrl}${CRM.communication.callLog.clientCallLog}`,
            { client_Ids: clientIds, ...pagination, ...searchParam }
          )
        : await axios.get(`${CRM.baseUrl}${CRM.communication.callLog.getAll}`, {
            params: {
              ...pagination,
              ...searchParam,
              ...(options.customerId != null && options.customerId !== ''
                ? { customerId: Number(options.customerId) }
                : {}),
              ...(options.projectId != null && options.projectId !== ''
                ? { projectId: Number(options.projectId) }
                : {}),
            },
          });

    const { rows, count, directionCounts } = parseListResponse(response.data);
    return { error: false, data: rows, count, directionCounts };
  } catch {
    return errorResult(CALL_HISTORY_EMPTY_MESSAGES.loadError);
  }
};

export const fetchCallHistory = (options: CallLogListOptions = {}) =>
  fetchCallHistoryFromCrm(options);

export const fetchCustomerCallHistory = (
  customerId: string | number,
  options: CallLogListOptions = {}
) => fetchCallHistoryFromCrm({ ...options, customerId });

export const fetchProjectCallHistory = (
  projectId: string | number,
  options: CallLogListOptions = {}
) => fetchCallHistoryFromCrm({ ...options, projectId });

export const fetchCallHistoryStats = async (
  options: Pick<CallLogListOptions, 'clientIds' | 'customerId'> = {}
): Promise<CallHistoryStats> => {
  if (!isConfigured()) return CALL_HISTORY_EMPTY_STATS;

  const startOfDay = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
  const clientIds = (options.clientIds || []).filter(Boolean);

  try {
    const response =
      clientIds.length > 0
        ? await axios.post(
            `${CRM.baseUrl}${CRM.communication.callLog.callStats}`,
            { client_Ids: clientIds, startOfDay }
          )
        : await axios.get(
            `${CRM.baseUrl}${CRM.communication.callLog.callStats}`,
            {
              params: {
                startOfDay,
                ...(options.customerId != null && options.customerId !== ''
                  ? { customerId: Number(options.customerId) }
                  : {}),
              },
            }
          );

    const data = response.data?.data ?? response.data;
    return {
      todayCalls: data?.todayCalls ?? 0,
      missedCalls: data?.missedCalls ?? 0,
      avgDuration: data?.avgDuration ?? '0:00',
    };
  } catch {
    return CALL_HISTORY_EMPTY_STATS;
  }
};

export const fetchCustomerDetailsByPhone = async (
  phone: string
): Promise<{ customerId: number; name: string } | null> => {
  if (!isConfigured()) return null;

  const digits = formatPhoneNumber(phone);
  if (!digits) return null;

  try {
    const response = await axios.get<{
      customer_id?: number;
      customer_name?: string;
    }>(`${CRM.baseUrl}${CRM.communication.callLog.getCustomerDetails}`, {
      params: { phone_number: digits },
    });
    const customerId = Number(response.data?.customer_id);
    const name = String(response.data?.customer_name || '').trim();
    if (!Number.isFinite(customerId) || !name) return null;
    return { customerId, name };
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response
      ?.status;
    if (status === 404) return null;
    return null;
  }
};

const SYNCED_CALL_IDS_KEY = 'pf:synced-rc-call-ids';
const MAX_PERSISTED_SYNCED_IDS = 500;
const syncedCallIds = new Set<string>();
const inFlightCallIds = new Set<string>();

const loadSyncedCallIds = () => {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(SYNCED_CALL_IDS_KEY);
    if (!raw) return;
    const ids = JSON.parse(raw) as string[];
    if (Array.isArray(ids)) {
      ids.forEach(id => syncedCallIds.add(String(id)));
    }
  } catch {
    return;
  }
};

const persistSyncedCallIds = () => {
  if (typeof window === 'undefined') return;
  try {
    const ids = Array.from(syncedCallIds).slice(-MAX_PERSISTED_SYNCED_IDS);
    sessionStorage.setItem(SYNCED_CALL_IDS_KEY, JSON.stringify(ids));
  } catch {
    return;
  }
};

loadSyncedCallIds();

const hasProcessedCallKey = (key: string) => syncedCallIds.has(key);

const hasAnyProcessedCallKey = (keys: string[]) =>
  keys.some(key => hasProcessedCallKey(key));

const isAnyCallKeyInFlight = (keys: string[]) =>
  keys.some(key => inFlightCallIds.has(key));

export const isCallRecordSynced = (record: RingCentralCallLogRecord) =>
  hasAnyProcessedCallKey(collectCallDedupeKeys(record));

export const markCallIdsAsSynced = (
  ...ids: Array<string | null | undefined>
) => {
  ids.filter(Boolean).forEach(id => syncedCallIds.add(String(id)));
  persistSyncedCallIds();
};

export const markCallRecordAsProcessed = (
  record?: RingCentralCallLogRecord | null,
  callId?: string | null
) => {
  markCallIdsAsSynced(...collectCallDedupeKeys(record, callId));
};

export const getCallLogMatcherData = (
  sessionIds: string[]
): Record<string, { id: string; note: string }[]> => {
  const data: Record<string, { id: string; note: string }[]> = {};
  sessionIds.forEach(sessionId => {
    if (syncedCallIds.has(sessionId)) {
      data[sessionId] = [{ id: sessionId, note: '' }];
    }
  });
  return data;
};

const isValidRecordingBlob = (blob: Blob) =>
  blob.size > 0 && blob.type !== 'text/html';

const resolveRecordingBlobForPayload = async (
  payload: CallLogDbPayload
): Promise<Blob | null> => {
  if (!payload.rc_recording_source) return null;

  try {
    const blob = await fetchCallRecordingBlob(
      payload.rc_recording_source,
      payload.rc_recording_id
    );
    return blob && isValidRecordingBlob(blob) ? blob : null;
  } catch {
    return null;
  }
};

export const postCallLogToCrm = async (
  payload: CallLogDbPayload,
  options: {
    skipRefresh?: boolean;
    record?: RingCentralCallLogRecord | null;
  } = {}
): Promise<boolean> => {
  if (!isConfigured()) {
    return false;
  }

  const callId = payload.call_id;
  if (!callId) {
    return false;
  }
  if (!payload.caller_phone && !payload.receiver_phone) {
    return false;
  }

  const dedupeKeys = collectCallDedupeKeys(options.record ?? null, callId);
  if (hasAnyProcessedCallKey(dedupeKeys)) {
    return true;
  }
  if (isAnyCallKeyInFlight(dedupeKeys)) {
    return hasAnyProcessedCallKey(dedupeKeys);
  }

  dedupeKeys.forEach(key => inFlightCallIds.add(key));
  const url = `${CRM.baseUrl}${CRM.communication.callLog.create}`;
  let postAttempted = false;
  try {
    const recording = await resolveRecordingBlobForPayload(payload);
    const body = buildCallLogPostBody(payload, recording);
    postAttempted = true;
    await axios.post(url, body, {
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        },
      ],
    });
    triggerCallLoggerMatch(dedupeKeys);
    if (!options.skipRefresh) {
      dispatchCallHistoryRefresh();
    }

    return true;
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response
      ?.status;
    if (status === 409 || status === 400) {
      return true;
    }
    return false;
  } finally {
    if (postAttempted) {
      markCallIdsAsSynced(...dedupeKeys);
    }
    dedupeKeys.forEach(key => inFlightCallIds.delete(key));
  }
};

const postRcRecordToCrm = async (
  record: RingCentralCallLogRecord
): Promise<boolean> => {
  if (!isCallLogRecordReady(record)) {
    return false;
  }

  const isInbound = isInboundCallDirection(record.direction);
  const hadOutboundContext = !isInbound && hasOutboundCallContext();
  const hadInboundContext = isInbound && hasInboundCallContext();

  const clearSessionContexts = () => {
    if (hadOutboundContext) {
      clearOutboundCallContext();
    }
    if (hadInboundContext) {
      clearInboundCallContext();
    }
  };

  if (isCallRecordSynced(record)) {
    clearSessionContexts();
    return false;
  }

  cacheTokenizedRecording(record);

  const customerId = isInbound ? null : getCallLogCustomerId();
  const projectId = isInbound
    ? getInboundCallLogProjectId()
    : getCallLogProjectId();
  const payload = mapRcCallLogToDbPayload(record, customerId, projectId);
  if (!payload) {
    clearSessionContexts();
    return false;
  }

  try {
    const saved = await postCallLogToCrm(payload, {
      skipRefresh: true,
      record,
    });
    if (saved) {
      triggerCallLoggerMatch(collectCallDedupeKeys(record, payload.call_id));
    }
    return saved;
  } finally {
    clearSessionContexts();
  }
};

/** POST new RingCentral call-log records to CRM (deduped, one list refresh). */
export const syncRcCallRecords = async (
  records: RingCentralCallLogRecord[]
): Promise<void> => {
  if (!records.length) {
    return;
  }
  if (!isConfigured()) {
    return;
  }

  let savedAny = false;
  for (const record of records) {
    if (await postRcRecordToCrm(record)) {
      savedAny = true;
    }
  }
  if (savedAny) {
    dispatchCallHistoryRefresh();
  }
};
